import { useState } from 'react';
import { X, Search, CheckCircle, AlertCircle } from 'lucide-react';
import { submitFeedback, searchHTS } from '../services/api';
import { ClassificationResult } from '../services/api';
import toast from 'react-hot-toast';

interface FeedbackModalProps {
    classification: ClassificationResult;
    onClose: () => void;
}

export default function FeedbackModal({ classification, onClose }: FeedbackModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{
        code: string;
        code_formatted: string;
        description: string;
        duty_rate: number;
    }>>([]);
    const [selectedCode, setSelectedCode] = useState('');
    const [selectedDescription, setSelectedDescription] = useState('');
    const [note, setNote] = useState('');
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSearch = async () => {
        if (searchQuery.length < 2) return;
        setSearching(true);
        try {
            const results = await searchHTS(searchQuery);
            setSearchResults(results);
        } catch {
            toast.error('搜索失败，请重试');
        } finally {
            setSearching(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedCode) {
            toast.error('请选择正确的 HTS 编码');
            return;
        }
        setSubmitting(true);
        try {
            await submitFeedback({
                classification_id: classification.id,
                correct_hts_code: selectedCode,
                note,
            });
            setSubmitted(true);
            setTimeout(onClose, 2000);
        } catch {
            toast.error('提交失败，请重试');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative glass rounded-2xl w-full max-w-lg animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
                    <div>
                        <h2 className="text-base font-semibold text-white">纠正 HTS 分类</h2>
                        <p className="text-xs text-slate-500 mt-0.5">您的反馈将帮助改进 AI 模型</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {submitted ? (
                        <div className="flex flex-col items-center py-8 gap-3 animate-fade-in">
                            <CheckCircle size={40} className="text-emerald-400" />
                            <p className="text-white font-medium">感谢您的反馈！</p>
                            <p className="text-slate-500 text-sm text-center">您的纠错已保存，将用于优化 AI 模型</p>
                        </div>
                    ) : (
                        <>
                            {/* Current classification */}
                            <div className="bg-surface-2 rounded-xl p-3 space-y-1">
                                <p className="text-xs text-slate-500">当前 AI 分类结果</p>
                                <p className="font-mono text-amber-400 text-sm">{classification.hts_code_formatted}</p>
                                <p className="text-xs text-slate-400 line-clamp-2">{classification.hts_description}</p>
                            </div>

                            {/* Search */}
                            <div>
                                <label className="section-header block">搜索正确的 HTS 编码</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="例：笔记本电脑 或 8471..."
                                        className="input-field flex-1 text-sm"
                                    />
                                    <button
                                        onClick={handleSearch}
                                        disabled={searching}
                                        className="btn-secondary px-4 text-sm"
                                    >
                                        <Search size={15} />
                                    </button>
                                </div>

                                {searchResults.length > 0 && (
                                    <div className="mt-2 max-h-48 overflow-y-auto space-y-1 rounded-xl bg-surface-1 p-1 border border-white/[0.06]">
                                        {searchResults.map((r) => (
                                            <button
                                                key={r.code}
                                                onClick={() => {
                                                    setSelectedCode(r.code);
                                                    setSelectedDescription(r.description);
                                                    setSearchResults([]);
                                                }}
                                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${selectedCode === r.code ? 'bg-brand-600/20 border border-brand-500/30' : 'hover:bg-white/[0.04]'
                                                    }`}
                                            >
                                                <span className="font-mono text-brand-300">{r.code_formatted}</span>
                                                <span className="text-slate-400 ml-2 text-xs line-clamp-1">{r.description}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {selectedCode && (
                                    <div className="mt-2 flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
                                        <CheckCircle size={15} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-slate-400">已选择：</p>
                                            <p className="text-sm font-mono text-emerald-400">{selectedCode.replace(/(\d{4})(\d{2})(\d{4})/, '$1.$2.$3')}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{selectedDescription}</p>
                                        </div>
                                    </div>
                                )}

                                {!selectedCode && (
                                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                        <AlertCircle size={13} />
                                        <span>或直接输入 HTS 编码：</span>
                                        <input
                                            type="text"
                                            placeholder="8471.30.0100"
                                            className="bg-surface-2 border border-white/10 rounded-lg px-2 py-1 text-xs text-white w-32 outline-none focus:border-brand-500"
                                            onChange={(e) => setSelectedCode(e.target.value.replace(/\./g, ''))}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Optional note */}
                            <div>
                                <label className="section-header block">备注（选填）</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="请说明分类不正确的原因"
                                    rows={2}
                                    className="input-field text-sm resize-none"
                                />
                            </div>

                            {/* Submit */}
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !selectedCode}
                                className="btn-primary w-full justify-center"
                            >
                                {submitting ? '提交中...' : '提交纠错'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
